import { BehaviorSubject, Observable, of } from 'rxjs';
import { delayWhen, filter, finalize, switchMap } from 'rxjs/operators';

/**
 * Enables queuing of multiple actions to ensure sequential processing
 */
export class ActionQueue {
  /**
   * List of ids in the queue
   */
  queue$: Observable<string[]>;

  /**
   * Id of item to be processed
   */
  currentActionId$: Observable<string>;

  private _queue$ = new BehaviorSubject<string[]>([]);
  private _currentActionId$ = new BehaviorSubject<string>(null);

  constructor() {
    this.queue$ = this._queue$.asObservable();
    this.currentActionId$ = this._currentActionId$.asObservable();
  }

  /**
   * Returns an action that is delayed
   * @param action$ action to be performed when item is dequeued
   */
  enqueue<T>(action$: Observable<T>): Observable<T> {
    const id = this._enqueue();

    // Start processing if no action is being processed
    if (!this._currentActionId$.value) {
      this._processNextAction();
    }

    // 1. Delay until id is next in queue
    // 2. Perform action
    // 3. Process next item in queue, even if action throws an error
    return of(id).pipe(
      delayWhen(() =>
        this._currentActionId$.pipe(filter(actionId => actionId === id || !actionId))
      ),
      switchMap(() => action$),
      finalize(() => this._processNextAction())
    );
  }

  private _processNextAction() {
    // If queue is empty, reset current action and return
    if (this._queue$.value.length === 0) {
      if (this._currentActionId$.value) {
        this._currentActionId$.next(null);
      }
      return;
    }

    const id = this._dequeue();
    this._currentActionId$.next(id);
  }

  /**
   * Puts a new id in the queue and returns it
   */
  private _enqueue(): string {
    const id = `${Math.random() * 1000000}`;
    this._queue$.value.push(id);
    this._queue$.next(this._queue$.value);
    return id;
  }

  /**
   * Dequeues the next id in queue and returns it
   */
  private _dequeue(): string {
    const id = this._queue$.value.shift();
    this._queue$.next(this._queue$.value);
    return id;
  }
}
